<template>
  <v-container fluid class="py-5 px-5 py-sm-10 px-sm-10 fill-height align-start">
    <v-row class="w-100">
      <!-- Page Header -->
      <v-col cols="12" class="d-flex flex-wrap align-center justify-space-between mb-2">
        <div class="text-start">
          <div class="textLight--text font-weight-bold text-h5 d-flex align-center">
            <v-icon color="primary" class="me-2" size="28">mdi-shield-account-outline</v-icon>
            Active Sessions & Security
          </div>
          <div class="textLight--text text-caption font-weight-light mt-1">
            Monitor, inspect, and disconnect devices currently signed into the platform
          </div>
        </div>

        <div class="d-flex align-center flex-wrap mt-3 mt-sm-0">
          <v-btn
            depressed
            outlined
            color="primary"
            class="me-3 rounded-lg text-capitalize"
            @click="loadSessions"
            :loading="loading"
          >
            <v-icon left size="18">mdi-refresh</v-icon>
            Refresh
          </v-btn>

          <v-btn
            depressed
            outlined
            color="primary"
            class="me-3 rounded-lg text-capitalize"
            :to="{ name: 'LoginAudit' }"
          >
            <v-icon left size="18">mdi-shield-search</v-icon>
            Login Audit Log
          </v-btn>

          <v-btn
            depressed
            color="error"
            class="rounded-lg text-capitalize"
            @click="confirmRevokeAllDialog = true"
            :disabled="loading || myOtherSessionsCount === 0"
          >
            <v-icon left size="18">mdi-logout-variant</v-icon>
            Disconnect My Other Sessions ({{ myOtherSessionsCount }})
          </v-btn>
        </div>
      </v-col>

      <!-- Overview Stats -->
      <v-col cols="12" sm="6" md="4">
        <v-card flat class="pa-4 rounded-xl border" :color="$vuetify.theme.dark ? '#1E1E1E' : '#F9FAFB'">
          <div class="d-flex align-center justify-space-between">
            <div>
              <div class="text-caption textLight--text font-weight-medium">Total Active Sessions</div>
              <div class="text-h4 font-weight-bold primary--text mt-1">{{ sessions.length }}</div>
            </div>
            <v-avatar color="primary lighten-5" size="48">
              <v-icon color="primary" size="26">mdi-devices</v-icon>
            </v-avatar>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="4">
        <v-card flat class="pa-4 rounded-xl border" :color="$vuetify.theme.dark ? '#1E1E1E' : '#F9FAFB'">
          <div class="d-flex align-center justify-space-between">
            <div>
              <div class="text-caption textLight--text font-weight-medium">Your Current Device</div>
              <div class="text-subtitle-1 font-weight-bold success--text mt-1 text-truncate" style="max-width: 220px">
                {{ currentSession ? `${currentSession.browser} (${currentSession.os})` : 'Connected' }}
              </div>
            </div>
            <v-avatar color="success lighten-5" size="48">
              <v-icon color="success" size="26">mdi-check-decagram</v-icon>
            </v-avatar>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="4">
        <v-card flat class="pa-4 rounded-xl border" :color="$vuetify.theme.dark ? '#1E1E1E' : '#F9FAFB'">
          <div class="d-flex align-center justify-space-between">
            <div>
              <div class="text-caption textLight--text font-weight-medium">My Other Remote Sessions</div>
              <div class="text-h4 font-weight-bold warning--text mt-1">{{ myOtherSessionsCount }}</div>
            </div>
            <v-avatar color="warning lighten-5" size="48">
              <v-icon color="warning" size="26">mdi-laptop-account</v-icon>
            </v-avatar>
          </div>
        </v-card>
      </v-col>

      <!-- Filter Controls & Search -->
      <v-col cols="12" class="d-flex flex-wrap align-center justify-space-between mt-2">
        <v-btn-toggle
          v-model="sessionFilter"
          mandatory
          dense
          color="primary"
          class="rounded-lg border mb-2 mb-sm-0"
        >
          <v-btn value="all" class="text-capitalize px-4">
            <v-icon left size="16">mdi-account-group-outline</v-icon>
            All Active Sessions ({{ sessions.length }})
          </v-btn>
          <v-btn value="mine" class="text-capitalize px-4">
            <v-icon left size="16">mdi-account-outline</v-icon>
            My Sessions ({{ mySessions.length }})
          </v-btn>
        </v-btn-toggle>

        <div style="max-width: 320px; width: 100%">
          <v-text-field
            v-model="searchQuery"
            dense
            outlined
            rounded
            hide-details
            placeholder="Search by user, IP, location..."
            prepend-inner-icon="mdi-magnify"
            clearable
          ></v-text-field>
        </div>
      </v-col>

      <!-- Sessions Table -->
      <v-col cols="12" class="mt-2">
        <v-card flat class="rounded-xl border">
          <v-data-table
            :headers="headers"
            :items="filteredSessions"
            :loading="loading"
            class="elevation-0"
            :items-per-page="15"
            no-data-text="No active sessions found"
          >
            <!-- Device & Browser -->
            <template v-slot:item.device="{ item }">
              <div class="d-flex align-center py-2 text-start">
                <v-avatar :color="item.isCurrent ? 'success lighten-5' : 'primary lighten-5'" size="38" class="me-3">
                  <v-icon :color="item.isCurrent ? 'success' : 'primary'" size="20">
                    {{ getDeviceIcon(item.os) }}
                  </v-icon>
                </v-avatar>
                <div>
                  <div class="font-weight-bold text-body-2 d-flex align-center">
                    {{ item.browser && item.browser !== 'Unknown Browser' ? item.browser : 'Web Browser' }}
                    <v-chip
                      v-if="item.isCurrent"
                      x-small
                      color="success"
                      text-color="white"
                      class="ms-2 font-weight-bold"
                    >
                      THIS DEVICE
                    </v-chip>
                  </div>
                  <div class="text-caption textLight--text font-weight-light">
                    {{ item.os && item.os !== 'Unknown OS' ? item.os : 'Desktop / Laptop' }}
                  </div>
                </div>
              </div>
            </template>

            <!-- User -->
            <template v-slot:item.user="{ item }">
              <div class="text-start">
                <div class="font-weight-medium text-body-2">{{ item.userName }}</div>
                <div class="text-caption textLight--text">{{ item.userEmail }}</div>
              </div>
            </template>

            <!-- Location & IP -->
            <template v-slot:item.location="{ item }">
              <div class="text-start">
                <div class="font-weight-medium text-body-2 d-flex align-center">
                  <v-icon size="14" class="me-1" color="grey">mdi-map-marker-outline</v-icon>
                  {{ formatLocation(item) }}
                </div>
                <div class="text-caption textLight--text font-monospace">
                  {{ item.ipAddress && item.ipAddress !== 'Unknown' ? item.ipAddress : 'Offline / Idle' }}
                </div>
              </div>
            </template>

            <!-- Last Active -->
            <template v-slot:item.lastActiveAt="{ item }">
              <div class="text-start">
                <div class="text-body-2">{{ formatDate(item.lastActiveAt) }}</div>
                <div class="text-caption success--text font-weight-medium">
                  <v-icon x-small color="success" class="me-1">mdi-circle</v-icon>
                  Active
                </div>
              </div>
            </template>

            <!-- Actions -->
            <template v-slot:item.actions="{ item }">
              <div class="text-end">
                <v-btn
                  v-if="!item.isCurrent"
                  small
                  depressed
                  color="error"
                  class="rounded-md text-capitalize px-3"
                  @click="openRevokeDialog(item)"
                  :loading="revokingId === item.id"
                >
                  <v-icon left size="14">mdi-power</v-icon>
                  Disconnect
                </v-btn>
                <span v-else class="text-caption success--text font-weight-bold pe-2">
                  <v-icon x-small color="success" class="me-1">mdi-check-circle</v-icon>
                  Current Session
                </span>
              </div>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- Confirm Single Revoke Dialog -->
    <v-dialog v-model="confirmRevokeDialog" max-width="450">
      <v-card class="pa-6 rounded-xl">
        <div class="text-center">
          <v-avatar color="error lighten-5" size="56" class="mb-3">
            <v-icon color="error" size="30">mdi-alert-circle-outline</v-icon>
          </v-avatar>
          <div class="text-h6 font-weight-bold">Disconnect This Session?</div>
          <div class="text-body-2 textLight--text mt-2" v-if="selectedSession">
            This will immediately log out <b>{{ selectedSession.userName }}</b> from
            <b>{{ selectedSession.browser }} on {{ selectedSession.os }}</b>
            ({{ selectedSession.ipAddress || 'Idle' }}).
          </div>
        </div>
        <div class="d-flex justify-end mt-6">
          <v-btn text class="me-2 text-capitalize" @click="confirmRevokeDialog = false">Cancel</v-btn>
          <v-btn color="error" depressed class="rounded-lg text-capitalize" @click="executeRevoke">
            Yes, Disconnect
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Confirm Revoke All Dialog -->
    <v-dialog v-model="confirmRevokeAllDialog" max-width="480">
      <v-card class="pa-6 rounded-xl">
        <div class="text-center">
          <v-avatar color="error lighten-5" size="56" class="mb-3">
            <v-icon color="error" size="30">mdi-logout-variant</v-icon>
          </v-avatar>
          <div class="text-h6 font-weight-bold">Disconnect My Other Sessions?</div>
          <div class="text-body-2 textLight--text mt-2">
            Every other device and browser currently logged into your admin account will be immediately terminated.
            Your current device will remain signed in.
          </div>
        </div>
        <div class="d-flex justify-end mt-6">
          <v-btn text class="me-2 text-capitalize" @click="confirmRevokeAllDialog = false">Cancel</v-btn>
          <v-btn color="error" depressed class="rounded-lg text-capitalize" @click="executeRevokeAll">
            Yes, Disconnect All Others
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import moment from "moment";
import {
  getActiveSessions,
  revokeSession,
  revokeAllOtherSessions,
} from "../methods/sessionMethods";

export default {
  name: "ActiveSessionsPage",
  data() {
    return {
      loading: false,
      sessions: [],
      sessionFilter: "all",
      searchQuery: "",
      revokingId: null,
      confirmRevokeDialog: false,
      confirmRevokeAllDialog: false,
      selectedSession: null,
      headers: [
        { text: "Device & Browser", value: "device", sortable: false },
        { text: "User Account", value: "user", sortable: false },
        { text: "IP & Location", value: "location", sortable: false },
        { text: "Last Activity", value: "lastActiveAt", sortable: true },
        { text: "Actions", value: "actions", sortable: false, align: "end" },
      ],
    };
  },
  computed: {
    currentUserId() {
      return this.currentSession?.userId || this.$store.state.authenticatedUser?._id;
    },
    currentSession() {
      return this.sessions.find(s => s.isCurrent);
    },
    mySessions() {
      if (!this.currentUserId) return this.sessions.filter(s => s.isCurrent);
      return this.sessions.filter(s => String(s.userId) === String(this.currentUserId));
    },
    myOtherSessionsCount() {
      return this.mySessions.filter(s => !s.isCurrent).length;
    },
    filteredSessions() {
      let list = this.sessionFilter === "mine" ? this.mySessions : this.sessions;
      if (this.searchQuery && this.searchQuery.trim()) {
        const q = this.searchQuery.toLowerCase().trim();
        list = list.filter(s =>
          (s.userName && s.userName.toLowerCase().includes(q)) ||
          (s.userEmail && s.userEmail.toLowerCase().includes(q)) ||
          (s.ipAddress && s.ipAddress.toLowerCase().includes(q)) ||
          (s.city && s.city.toLowerCase().includes(q)) ||
          (s.country && s.country.toLowerCase().includes(q)) ||
          (s.browser && s.browser.toLowerCase().includes(q)) ||
          (s.os && s.os.toLowerCase().includes(q))
        );
      }
      return list;
    },
  },
  mounted() {
    this.loadSessions();
  },
  methods: {
    async loadSessions() {
      this.loading = true;
      try {
        const res = await getActiveSessions();
        if (res.data) {
          this.sessions = res.data;
        }
      } catch (e) {
        console.error("Error loading active sessions:", e);
      } finally {
        this.loading = false;
      }
    },
    openRevokeDialog(session) {
      this.selectedSession = session;
      this.confirmRevokeDialog = true;
    },
    async executeRevoke() {
      if (!this.selectedSession) return;
      this.confirmRevokeDialog = false;
      this.revokingId = this.selectedSession.id;

      try {
        const res = await revokeSession(this.selectedSession.id);
        if (res.data) {
          this.$toast?.success ? this.$toast.success("Session disconnected successfully") : null;
          await this.loadSessions();
        } else {
          this.$toast?.error ? this.$toast.error("Failed to disconnect session") : null;
        }
      } catch (e) {
        console.error("Error revoking session:", e);
      } finally {
        this.revokingId = null;
      }
    },
    async executeRevokeAll() {
      this.confirmRevokeAllDialog = false;
      this.loading = true;

      try {
        const res = await revokeAllOtherSessions();
        if (res.data) {
          this.$toast?.success ? this.$toast.success("All other sessions disconnected") : null;
          await this.loadSessions();
        } else {
          this.$toast?.error ? this.$toast.error("Failed to disconnect sessions") : null;
        }
      } catch (e) {
        console.error("Error revoking all sessions:", e);
      } finally {
        this.loading = false;
      }
    },
    getDeviceIcon(os) {
      if (!os) return "mdi-laptop";
      const lower = os.toLowerCase();
      if (lower.includes("android") || lower.includes("ios") || lower.includes("iphone")) {
        return "mdi-cellphone";
      }
      if (lower.includes("mac")) return "mdi-apple";
      if (lower.includes("windows")) return "mdi-microsoft-windows";
      if (lower.includes("linux")) return "mdi-linux";
      return "mdi-laptop";
    },
    formatLocation(item) {
      const parts = [];
      if (item.city) parts.push(item.city);
      if (item.country) parts.push(item.country);
      if (parts.length > 0) return parts.join(", ");
      if (item.ipAddress && item.ipAddress !== "Unknown" && item.ipAddress !== "") {
        return "Locating...";
      }
      return "Unknown Location";
    },
    formatDate(dateStr) {
      if (!dateStr) return "-";
      return moment(dateStr).calendar(null, {
        sameDay: "[Today at] h:mm A",
        lastDay: "[Yesterday at] h:mm A",
        lastWeek: "MMM D [at] h:mm A",
        sameElse: "MMM D, YYYY [at] h:mm A",
      });
    },
  },
};
</script>

<style scoped>
.border {
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
}
.font-monospace {
  font-family: monospace;
}
</style>
